import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, rupee } from "./Bits";
import { X, CalendarDays, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SHIFTS = ["Visit (2 hrs)", "Half Day (4 hrs)", "Full Day (8 hrs)", "Night (10 hrs)", "Live-in (24 hrs)"];

export default function BookingModal({ carer, open, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [shift, setShift] = useState(SHIFTS[2]);
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  if (!open || !carer) return null;

  const multiplier = { "Visit (2 hrs)": 1, "Half Day (4 hrs)": 2, "Full Day (8 hrs)": 4, "Night (10 hrs)": 5, "Live-in (24 hrs)": 8 };
  const amount = carer.rate * (multiplier[shift] || 1);

  const submit = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "care_seeker") { toast.error("Only care seekers can book. Log in as a family account."); return; }
    if (!date || !location) { toast.error("Please add a date and location."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        carer_id: carer.id, service_type: carer.role, shift, date, location, amount, notes,
      });
      setDone(data);
      toast.success("Booking requested!");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const close = () => { setDone(null); setDate(""); setLocation(""); setNotes(""); onClose(); };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/50 grid place-items-center p-4" onClick={close} data-testid="booking-modal">
      <div className="bg-card rounded-r-lg w-full max-w-md p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-bold">{done ? "Booking confirmed" : "Book a carer"}</h3>
          <button onClick={close} data-testid="booking-close"><X size={20} /></button>
        </div>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 size={56} className="text-sage mx-auto" />
            <p className="mt-4 font-semibold">{carer.name} has received your request.</p>
            <p className="text-sm text-ink-soft mt-1">Booking {done.id} · {shift}</p>
            <p className="text-sm text-ink-soft">You'll be notified once the carer confirms.</p>
            <div className="grid grid-cols-2 gap-2 mt-6">
              <button onClick={close} className="btn-pill justify-center border border-line px-4 py-2.5 text-sm">Later</button>
              <button onClick={() => nav(`/checkout/${done.id}`)} className="btn-pill justify-center bg-coral text-white px-4 py-2.5 text-sm" data-testid="goto-portal">Pay now</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mt-4 p-3 bg-ivory-2 rounded-r">
              <Avatar initials={carer.avatar_initials} gradient={carer.gradient} size={44} />
              <div>
                <p className="font-semibold text-ink">{carer.name}</p>
                <p className="text-xs text-coral">{carer.role}</p>
              </div>
            </div>

            <label className="block text-sm font-semibold mt-4 mb-1.5">Shift type</label>
            <div className="grid grid-cols-2 gap-2">
              {SHIFTS.map((s) => (
                <button key={s} onClick={() => setShift(s)} data-testid={`shift-${s}`}
                  className={`text-xs px-3 py-2 rounded-r border transition-colors ${shift === s ? "bg-coral text-white border-coral" : "border-line text-ink-soft hover:border-coral"}`}>
                  {s}
                </button>
              ))}
            </div>

            <label className="block text-sm font-semibold mt-4 mb-1.5">Date & time</label>
            <div className="relative">
              <CalendarDays size={16} className="absolute left-3 top-3 text-ink-faint" />
              <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="e.g. Tomorrow 8 AM"
                className="w-full border border-line rounded-r pl-9 pr-3 py-2.5 text-sm bg-ivory focus:outline-none focus:border-coral" data-testid="booking-date" />
            </div>

            <label className="block text-sm font-semibold mt-4 mb-1.5">Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Gachibowli, Hyderabad"
              className="w-full border border-line rounded-r px-3 py-2.5 text-sm bg-ivory focus:outline-none focus:border-coral" data-testid="booking-location" />

            <label className="block text-sm font-semibold mt-4 mb-1.5">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything the carer should know"
              className="w-full border border-line rounded-r px-3 py-2.5 text-sm bg-ivory focus:outline-none focus:border-coral resize-none" data-testid="booking-notes" />

            <div className="flex items-center justify-between mt-5">
              <div>
                <span className="text-xs text-ink-soft">Estimated total</span>
                <p className="font-display font-bold text-xl">{rupee(amount)}</p>
              </div>
              <button onClick={submit} disabled={loading} className="btn-pill bg-coral text-white px-6 py-3 disabled:opacity-60" data-testid="booking-confirm">
                {loading ? "Booking..." : "Confirm booking"}
              </button>
            </div>
            <p className="text-[11px] text-ink-faint mt-3 text-center">Funds held in escrow. Pay after the carer confirms — UPI, card or international for NRIs.</p>
          </>
        )}
      </div>
    </div>
  );
}
